import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, LogOut, Info, FileText, HelpCircle, ClipboardCheck, MessageSquare } from "lucide-react";
import { request } from "../service/HttpService";
import { ftAccessToken, ftUserPhoto } from "../constants/Constants";
import { logoutCall, getJwtIsReviewer } from "../service/AuthService";
import { PEER_REVIEW_ENABLED } from "../constants/Constants";

export default function UserMenu() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [userInfo, setUserInfo] = useState({ firstName: "", lastName: "", email: "" });
    const [photo, setPhoto] = useState(() => (localStorage.getItem(ftUserPhoto) ?? "").replaceAll('"', ''));
    const ref = useRef(null);
    // The user menu only makes sense for a signed-in user, and its /users/userinfo
    // fetch would 401 → token-refresh → hard-redirect to "/" for a logged-out
    // visitor. Public pages that reuse this header (e.g. the funder portal) render
    // it with no token, so skip the fetch and hide the menu entirely.
    const loggedIn = !!localStorage.getItem(ftAccessToken);

    useEffect(() => {
        if (!loggedIn) return;
        request("GET", "/users/userinfo")
            .then(res => setUserInfo(res))
            .catch(() => {});
    }, [loggedIn]);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const initials = `${userInfo.firstName?.[0] ?? ""}${userInfo.lastName?.[0] ?? ""}`;

    if (!loggedIn) return null;

    return (
        <div className="user-menu-wrap" ref={ref}>
            <button className="user-avatar user-avatar-btn" onClick={() => setOpen((o) => !o)}>
                {photo
                    ? <img src={photo} alt="Profile" className="user-avatar-img" />
                    : initials
                }
            </button>

            {open && (
                <div className="user-menu-dropdown">
                    <div className="user-menu-profile">
                        <div className="user-menu-avatar">
                            {photo
                                ? <img src={photo} alt="Profile" className="user-avatar-img" />
                                : initials
                            }
                        </div>
                        <div>
                            <p className="user-menu-name">{userInfo.firstName} {userInfo.lastName}</p>
                            <p className="user-menu-email">{userInfo.email}</p>
                        </div>
                    </div>

                    <div className="user-menu-divider" />

                    <div className="user-menu-mobile-links">
                        <button className="user-menu-item" onClick={() => { setOpen(false); navigate("/about"); }}>
                            <Info size={14} />
                            About Us
                        </button>
                        <a className="user-menu-item" href="/whitepaper.pdf" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
                            <FileText size={14} />
                            White Paper
                        </a>
                        <button className="user-menu-item" onClick={() => { setOpen(false); navigate("/faq"); }}>
                            <HelpCircle size={14} />
                            FAQ
                        </button>
                        <button className="user-menu-item" onClick={() => { setOpen(false); navigate("/feedback"); }}>
                            <MessageSquare size={14} />
                            Feedback
                        </button>
                        {PEER_REVIEW_ENABLED && getJwtIsReviewer() && (
                            <button className="user-menu-item" onClick={() => { setOpen(false); navigate("/reviewer"); }}>
                                <ClipboardCheck size={14} />
                                Review Queue
                            </button>
                        )}
                        <div className="user-menu-divider" />
                    </div>

                    <button className="user-menu-item" onClick={() => { setOpen(false); navigate("/settings"); }}>
                        <Settings size={14} />
                        Settings
                    </button>
                    <button className="user-menu-item user-menu-item-danger" onClick={() => { setOpen(false); logoutCall(true); navigate("/"); }}>
                        <LogOut size={14} />
                        Log Out
                    </button>
                </div>
            )}
        </div>
    );
}
